import jsPDF from "jspdf";

export const generateTaxFormPDF = (formQuestions, answers, comments, formNumber, companyLogo) => {
  const doc = new jsPDF();

  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();

  const margin = {
    left: 15,
    right: 15,
    top: 25,  
    bottom: 20
  };

  const contentWidth = pageWidth - margin.left - margin.right;
  const headerStyles = {
    logo: { width: 40, height: 15, marginRight: 10 },
    title: {
      size: 16,
      style: 'bold',
      color: [0, 51, 102], 
      marginBottom: 3
    },
    formNumber: {
      size: 14,
      style: 'bold',
      color: [102, 0, 51] 
    },
    divider: { color: [200, 200, 200], width: 0.5 }
  };

  // Document styles
  const styles = {
    section: { size: 12, style: 'bold' },
    question: { size: 10, style: 'normal' },
    answer: { size: 10, style: 'bold' },
    comment: { size: 9, style: 'italic' }
  };

  // Line heights
  const lineHeights = {
    title: 8,
    section: 6,
    question: 5,
    answer: 5,
    comment: 4
  };

  // Current position
  let y = margin.top;

  // Add header to each page
  const addHeader = () => {
    // Reset styles
    doc.setFont("helvetica");
    doc.setTextColor(0, 0, 0);

    // Calculate positions
    const logoX = margin.left;
    const logoY = 10;

    // Add logo
    doc.addImage(
      companyLogo,
      "PNG",
      logoX,
      logoY,
      headerStyles.logo.width,
      headerStyles.logo.height
    );

    // Calculate title position to center it properly
    const titleText = "Tax Filing Checklist";
    doc.setFontSize(headerStyles.title.size);
    doc.setFont("helvetica", headerStyles.title.style);
    doc.setTextColor(...headerStyles.title.color);

    const titleWidth = doc.getStringUnitWidth(titleText) * headerStyles.title.size / doc.internal.scaleFactor;
    const titleX = (pageWidth - titleWidth) / 2;
    const titleY = logoY + headerStyles.logo.height / 2 + headerStyles.title.size / 2;

    doc.text(titleText, titleX, titleY);

    // Add form number on the right
    const formText = `Form: ${formNumber}`;
    doc.setFontSize(headerStyles.formNumber.size);
    doc.setFont("helvetica", headerStyles.formNumber.style);
    doc.setTextColor(...headerStyles.formNumber.color);

    const formX = pageWidth - margin.right - 5;
    const formY = logoY + headerStyles.logo.height / 2 + headerStyles.formNumber.size / 2;

    doc.text(formText, formX, formY, { align: 'right' });

    // Add divider line below header
    const dividerY = titleY + headerStyles.title.marginBottom;
    doc.setDrawColor(...headerStyles.divider.color);
    doc.setLineWidth(headerStyles.divider.width);
    doc.line(margin.left, dividerY, pageWidth - margin.right, dividerY);

    // Reset y position for content (after header)
    y = dividerY + 5;
  };

  // Add footer to each page
  const addFooter = () => {
    doc.setFontSize(8);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(100);

    // Footer line
    doc.setDrawColor(200, 200, 200);
    doc.line(margin.left, pageHeight - 15, pageWidth - margin.right, pageHeight - 15);

    // Page number
    doc.text(
      `Page ${doc.internal.getCurrentPageInfo().pageNumber}`,
      pageWidth - margin.right - 5,
      pageHeight - 10,
      { align: 'right' }
    );
  };

  // Check if we need a new page
  const checkNewPage = (requiredSpace) => {
    if (y + requiredSpace > pageHeight - margin.bottom) {
      addFooter();
      doc.addPage();
      y = margin.top;
      addHeader();
    }
  };

  // Add text with proper wrapping
  const addText = (text, options = {}) => {
    const {
      size = 10,
      style = 'normal',
      indent = 0,
      color = [0, 0, 0],
      maxLines = null
    } = options;

    doc.setFontSize(size);
    doc.setFont("helvetica", style);
    doc.setTextColor(...color);

    const x = margin.left + indent;
    const lineHeight = size * 0.35;
    const splitText = doc.splitTextToSize(text, contentWidth - indent);

    const textToAdd = maxLines ? splitText.slice(0, maxLines) : splitText;

    for (let i = 0; i < textToAdd.length; i++) {
      checkNewPage(lineHeight);
      doc.text(textToAdd[i], x, y);
      y += lineHeight;
    }

    if (maxLines && splitText.length > maxLines) {
      return splitText.slice(maxLines).join(' ');
    }

    return null;
  };

  // Add header to first page
  addHeader();

  // Process each section
  Object.keys(formQuestions).forEach((section, sectionIndex) => {
    const sectionLetter = String.fromCharCode(65 + sectionIndex);
    const sectionTitle = `${sectionLetter}. ${section}`;

    // Add section title
    checkNewPage(lineHeights.section * 2);
    addText(sectionTitle, {
      size: styles.section.size,
      style: styles.section.style
    });
    y += lineHeights.section;

    // Process each question
    formQuestions[section].forEach((question, questionIndex) => {
      const answer = answers[`${section}-${questionIndex}`] || 'Not answered';
      const comment = comments[`${section}-${questionIndex}`] || '';

      // Add question
      const questionText = `${questionIndex + 1}. ${question}`;
      checkNewPage(lineHeights.question * 3);
      addText(questionText, {
        size: styles.question.size,
        style: styles.question.style,
        indent: 5
      });
      y += lineHeights.question / 2;

      // Add answer
      addText(`Answer: ${answer.toUpperCase()}`, {
        size: styles.answer.size,
        style: styles.answer.style,
        indent: 10,
        color: answer === 'Not answered' ? [150, 150, 150] : [0, 0, 0]
      });
      y += lineHeights.answer;

      // Add comment if exists
      if (answer === 'comment' && comment) {
        addText(`Comment: ${comment}`, {
          size: styles.comment.size,
          style: styles.comment.style,
          indent: 15,
          color: [100, 100, 100]
        });
        y += lineHeights.comment;
      }

      // Space between questions
      y += lineHeights.question;
    });

    // Space between sections
    y += lineHeights.section;
  });

  // Add footer to last page
  addFooter();

  return doc.output("blob");
};